import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { aiAssessmentAPI, entitiesAPI, riskConfigAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { WorkflowHeader } from '../../components/shared';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { ArrowLeft, ArrowRight, Save, CheckCircle2, ChevronRight } from 'lucide-react';

import { STEPS, INITIAL_FORM_DATA } from './ai-assessment/constants';

// Step components
import Step1SystemInfo from './ai-assessment/steps/Step1SystemInfo';
import Step2UseCase from './ai-assessment/steps/Step2UseCase';
import Step3DataProcessing from './ai-assessment/steps/Step3DataProcessing';
import Step4InitialRisk from './ai-assessment/steps/Step4InitialRisk';
import Step5Controls from './ai-assessment/steps/Step5Controls';
import Step6ResidualRisk from './ai-assessment/steps/Step6ResidualRisk';
import Step7Review from './ai-assessment/steps/Step7Review';

export default function AIAssessmentWorkflowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDPO, user, isComplianceReadOnly: isReadOnly } = useAuth();

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [entities, setEntities] = useState([]);
  const [riskConfig, setRiskConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const assessmentIdRef = useRef(id || null);

  // ── Data Loading ────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [entitiesRes, configRes] = await Promise.all([
          isDPO ? entitiesAPI.getAll() : Promise.resolve({ data: [] }),
          riskConfigAPI.get('ai_risk').catch(() => null)
        ]);
        if (isDPO) setEntities(entitiesRes.data);
        if (configRes?.data) setRiskConfig(configRes.data.config);
        if (id) {
          const res = await aiAssessmentAPI.getOne(id);
          setFormData(res.data);
          setCurrentStep(res.data.current_step || 1);
          assessmentIdRef.current = id;
        }
      } catch { toast.error('Failed to load data'); }
      finally { setLoading(false); }
    };
    load();
  }, [id, isDPO]);

  const updateField = (field, value) => {
    if (isReadOnly) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ── Save & Risk Calculation ─────────────────────────────────────────────
  const saveProgress = async (showToast = true) => {
    try {
      setSaving(true);
      const payload = { ...formData, current_step: currentStep };
      if (!payload.organization_id && user?.organization_id) payload.organization_id = user.organization_id;
      if (assessmentIdRef.current) {
        await aiAssessmentAPI.update(assessmentIdRef.current, payload);
      } else {
        const res = await aiAssessmentAPI.create(payload);
        assessmentIdRef.current = res.data.id;
        updateField('ai_reference', res.data.ai_reference);
      }
      if (showToast) toast.success('Progress saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const calculateRisk = async (riskType) => {
    if (!assessmentIdRef.current) await saveProgress(false);
    const inputs = riskType === 'initial' ? formData.initial_risk_inputs : formData.residual_risk_inputs;
    try {
      setCalculating(true);
      const res = await aiAssessmentAPI.calculateRisk(assessmentIdRef.current, riskType, inputs);
      if (riskType === 'initial') {
        setFormData(prev => ({
          ...prev,
          initial_risk_score: res.data.score, initial_risk_level: res.data.level,
          initial_risk_label: res.data.label, initial_risk_action: res.data.action,
          initial_risk_factors: res.data.factors,
          initial_trustworthiness_score: res.data.trustworthiness_score,
          initial_data_protection_score: res.data.data_protection_score,
          dpia_recommended: res.data.dpia_recommended, dpia_trigger_reason: res.data.dpia_trigger_reason,
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          residual_risk_score: res.data.score, residual_risk_level: res.data.level,
          residual_risk_label: res.data.label, residual_risk_action: res.data.action,
          residual_risk_factors: res.data.factors,
          residual_trustworthiness_score: res.data.trustworthiness_score,
          residual_data_protection_score: res.data.data_protection_score,
        }));
      }
      toast.success(`${riskType === 'initial' ? 'Initial' : 'Residual'} risk calculated`);
    } catch { toast.error('Failed to calculate risk'); }
    finally { setCalculating(false); }
  };

  // ── Navigation ──────────────────────────────────────────────────────────
  const goToStep = (step) => { setCurrentStep(step); saveProgress(false); };
  const nextStep = () => { if (currentStep < 7) { setCurrentStep(currentStep + 1); saveProgress(false); } };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  // ── Step Renderer ───────────────────────────────────────────────────────
  const renderCurrentStep = () => {
    const common = { formData, updateField };
    switch (currentStep) {
      case 1: return <Step1SystemInfo {...common} entities={entities} />;
      case 2: return <Step2UseCase {...common} />;
      case 3: return <Step3DataProcessing {...common} />;
      case 4: return <Step4InitialRisk {...common} calculateRisk={calculateRisk} calculating={calculating} riskConfig={riskConfig} />;
      case 5: return <Step5Controls {...common} />;
      case 6: return <Step6ResidualRisk {...common} calculateRisk={calculateRisk} calculating={calculating} riskConfig={riskConfig} />;
      case 7: return <Step7Review {...common} />;
      default: return null;
    }
  };

  if (loading) return (
    <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div></DashboardLayout>
  );

  const progress = Math.round((currentStep / STEPS.length) * 100);

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="p-6" data-testid="ai-assessment-workflow-page">
        <WorkflowHeader
          title={id ? 'Edit AI Assessment' : 'New AI Assessment'}
          subtitle="NIST AI RMF + Data Protection Assessment"
          reference={formData.ai_reference}
          onBack={() => navigate('/risk/assessments')}
          progressPercentage={progress}
          autoSaveStatus={saving ? 'saving' : 'idle'}
          showRisk={formData.residual_risk_score !== null}
          riskScore={formData.residual_risk_score}
          riskTier={formData.residual_risk_level}
          riskTierLabel={formData.residual_risk_level}
          riskTierColor={
            formData.residual_risk_level === 'Critical' ? 'bg-red-600 text-white' :
            formData.residual_risk_level === 'High' ? 'bg-orange-500 text-white' :
            formData.residual_risk_level === 'Medium' ? 'bg-amber-500 text-white' :
            formData.residual_risk_level === 'Low' ? 'bg-green-500 text-white' : ''
          }
        >
          {!isReadOnly && (
            <Button variant="outline" size="sm" onClick={() => saveProgress()} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </WorkflowHeader>

        <div className="p-6">
          {/* Stepper */}
          <div className="mb-8 overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.num;
                const isComplete = currentStep > step.num;
                return (
                  <React.Fragment key={step.num}>
                    <button type="button" onClick={() => goToStep(step.num)}
                      className={cn("flex items-center gap-2 px-3 py-2 rounded-lg transition-all",
                        isActive ? "bg-primary text-primary-foreground" :
                        isComplete ? "bg-primary/10 text-primary" :
                        "bg-muted/50 text-muted-foreground hover:bg-muted")}>
                      <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                        isActive ? "bg-primary-foreground/20" : isComplete ? "bg-primary/20" : "bg-muted")}>
                        {isComplete ? <CheckCircle2 className="h-4 w-4" /> : step.num}
                      </div>
                      <span className="text-sm font-medium hidden sm:inline">{step.label}</span>
                    </button>
                    {index < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Step Content */}
          <div className="max-w-4xl">{renderCurrentStep()}</div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t max-w-4xl">
            <Button variant="outline" onClick={prevStep} disabled={currentStep === 1}>
              <ArrowLeft className="h-4 w-4 mr-2" />Previous
            </Button>
            <div className="flex items-center gap-2">
              {currentStep === 7 ? (
                !isReadOnly && (
                  <Button onClick={async () => { await saveProgress(); navigate('/risk/assessments'); }}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />Complete Assessment
                  </Button>
                )
              ) : (
                <Button onClick={nextStep}>Next<ArrowRight className="h-4 w-4 ml-2" /></Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
